import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Paperclip } from 'lucide-react';
import { StatusPill } from '@/shared/ui/status-pill';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { ErrorState } from '@/components/common';
import { formatDateTime } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { humanise } from '@/lib/format';
import { ROUTES } from '@/constants';
import { useReplyToTicket, useTicket, useUpdateTicket } from './queries';
import { TICKET_PRIORITIES, TICKET_STATUSES, type TicketDetail as Detail } from './types';

const REPLY_MAX = 5000;

/**
 * One ticket: what the lab wrote, the conversation, and the controls.
 *
 * A reply is emailed to whoever raised the ticket. "Reply and wait for the lab"
 * sends it and moves the ticket to waiting on user in one step - the usual
 * thing after asking a question. Resolving emails the lab too.
 */
export function TicketDetail({ ticketId }: { ticketId: string }) {
  const ticket = useTicket(ticketId);

  if (ticket.isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-7 w-2/3" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (ticket.isError) {
    return (
      <ErrorState
        title="Could not load this ticket"
        description={ticket.error.message}
        action={
          <Button asChild variant="outline">
            <Link to={ROUTES.tickets}>Back to tickets</Link>
          </Button>
        }
      />
    );
  }

  return <Loaded ticket={ticket.data} />;
}

function Loaded({ ticket }: { ticket: Detail }) {
  const reply = useReplyToTicket(ticket.id);
  const update = useUpdateTicket(ticket.id);
  const [text, setText] = useState('');

  const closed = ticket.status === 'closed';
  const done = ticket.status === 'resolved' || closed;
  const busy = reply.isPending || update.isPending;

  const send = (status?: 'waiting_on_user' | 'resolved') => {
    const body = text.trim();
    if (!body) return;
    reply.mutate(
      { body, ...(status ? { status } : {}) },
      {
        onSuccess: () => {
          setText('');
        },
      },
    );
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
      <div className="min-w-0 space-y-5">
        <div>
          <Link
            to={ROUTES.tickets}
            className="inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={14} /> All tickets
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[12px] text-muted-foreground">{ticket.ref}</span>
            <StatusPill status={ticket.status} />
            <StatusPill status={ticket.priority} />
          </div>
          <h2 className="mt-1 text-lg font-semibold">{ticket.subject}</h2>
        </div>

        <article className="rounded-lg border bg-card p-4">
          <p className="text-[12px] text-muted-foreground">
            {ticket.raisedBy?.name ?? 'The lab'} · {formatDateTime(ticket.createdAt)}
          </p>
          <p className="mt-2 whitespace-pre-wrap text-sm">
            {ticket.description ?? 'No description given.'}
          </p>
          {ticket.attachmentUrl && (
            <a
              href={ticket.attachmentUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-[12.5px] text-primary hover:underline"
            >
              <Paperclip size={13} /> Attachment
            </a>
          )}
        </article>

        <section aria-label="Conversation" className="space-y-3">
          {ticket.comments.length === 0 && (
            <p className="text-[12.5px] text-muted-foreground">No replies yet.</p>
          )}
          {ticket.comments.map((comment) => (
            <article
              key={comment.id}
              className={cn(
                'rounded-lg border p-4',
                comment.author === 'staff' ? 'ml-8 bg-muted/40' : 'mr-8 bg-card',
              )}
            >
              <p className="text-[12px] text-muted-foreground">
                <span className="font-medium text-foreground">{comment.authorName}</span>
                {comment.author === 'staff'
                  ? ' (NuDent support)'
                  : ` (${ticket.organization.name})`}
                {' · '}
                {formatDateTime(comment.createdAt)}
              </p>
              <p className="mt-1.5 whitespace-pre-wrap text-sm">{comment.body}</p>
            </article>
          ))}
        </section>

        {closed ? (
          <div className="rounded-lg border border-dashed p-4 text-[12.5px] text-muted-foreground">
            This ticket is closed. Reopen it to reply.
            <Button
              variant="outline"
              size="sm"
              className="ml-3"
              disabled={busy}
              onClick={() => {
                update.mutate({ status: 'open' });
              }}
            >
              Reopen
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <label htmlFor="ticket-reply" className="text-[12.5px] font-medium">
              Reply to{' '}
              {ticket.raisedBy ? `${ticket.raisedBy.name} (${ticket.raisedBy.email})` : 'the lab'}
            </label>
            <textarea
              id="ticket-reply"
              rows={5}
              value={text}
              maxLength={REPLY_MAX}
              onChange={(event) => {
                setText(event.target.value);
              }}
              placeholder="Write your reply. The lab gets it by email and sees it on their Issues page."
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={busy || !text.trim()}
                onClick={() => {
                  send();
                }}
              >
                Send reply
              </Button>
              <Button
                variant="outline"
                disabled={busy || !text.trim()}
                onClick={() => {
                  send('waiting_on_user');
                }}
              >
                Reply and wait for the lab
              </Button>
              {!done && (
                <Button
                  variant="outline"
                  disabled={busy || !text.trim()}
                  onClick={() => {
                    send('resolved');
                  }}
                >
                  Reply and resolve
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <aside className="space-y-4 rounded-lg border bg-card p-4 text-[12.5px] lg:self-start">
        <Fact label="Lab">{ticket.organization.name}</Fact>
        <Fact label="Raised by">
          {ticket.raisedBy ? (
            <>
              {ticket.raisedBy.name}
              <br />
              <a href={`mailto:${ticket.raisedBy.email}`} className="text-primary hover:underline">
                {ticket.raisedBy.email}
              </a>
            </>
          ) : (
            '-'
          )}
        </Fact>
        {ticket.reportedFor && <Fact label="On behalf of">{ticket.reportedFor.name}</Fact>}
        <Fact label="Category">{humanise(ticket.category)}</Fact>
        <Fact label="Raised">{formatDateTime(ticket.createdAt)}</Fact>
        {ticket.resolvedAt && (
          <Fact label={closed ? 'Closed' : 'Resolved'}>
            {formatDateTime(ticket.resolvedAt)}
            {ticket.resolvedBy ? ` by ${ticket.resolvedBy.name}` : ' by the lab'}
          </Fact>
        )}

        <div className="space-y-1.5">
          <p className="text-muted-foreground">Status</p>
          <Select
            value={ticket.status}
            disabled={busy}
            onValueChange={(value) => {
              update.mutate({ status: value as Detail['status'] });
            }}
          >
            <SelectTrigger className="h-8 w-full" aria-label="Status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TICKET_STATUSES.map((value) => (
                <SelectItem key={value} value={value} disabled={closed && value !== 'open'}>
                  {humanise(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <p className="text-muted-foreground">Priority</p>
          <Select
            value={ticket.priority}
            disabled={busy || closed}
            onValueChange={(value) => {
              update.mutate({ priority: value as Detail['priority'] });
            }}
          >
            <SelectTrigger className="h-8 w-full" aria-label="Priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TICKET_PRIORITIES.map((value) => (
                <SelectItem key={value} value={value}>
                  {humanise(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!done && (
          <Button
            className="w-full"
            variant="outline"
            disabled={busy}
            onClick={() => {
              update.mutate({ status: 'resolved' });
            }}
          >
            Mark resolved
          </Button>
        )}
        <p className="text-[11.5px] leading-relaxed text-muted-foreground">
          The lab is emailed when you reply and when the ticket is resolved.
        </p>
      </aside>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-medium">{children}</p>
    </div>
  );
}
